import React, { useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { ApiHook } from "../../../hooks/apiHook";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import SubmitButton from "../buttons/SubmitButton";

/**
 * أثناء حملة إعادة التعيين الأمنية (REACT_APP_SECURITY_NOTICE_UNTIL ما زال سارياً)
 * يمكن تعيين كلمة مرور المحفظة بدون الكلمة الحالية، عبر نقطة الجلسة المؤقتة.
 */
const isSecurityCampaignOpen = () => {
  const until = process.env.REACT_APP_SECURITY_NOTICE_UNTIL;
  if (!until) return false;
  const ts = Date.parse(until);
  return !Number.isNaN(ts) && Date.now() <= ts;
};

const ChangeTransPassModal = ({ showModal, onHide, passwordPolicy }) => {
  const { t } = useTranslation();
  const campaignMode = useMemo(() => isSecurityCampaignOpen(), []);

  const [formData, setFormdata] = useState({
    currentPassword: "",
    newPassword: "",
    passwordConfirm: "",
  });
  const [errorMessage, setErrorMessage] = useState({
    currentPassword: null,
    newPassword: null,
    passwordConfirm: null,
  });
  const changeTransactionPasswordMutation = ApiHook.CallChangeTransactionPassword();
  const securityResetWalletMutation = ApiHook.CallSecurityResetWalletBySession();
  const isSubmitting =
    changeTransactionPasswordMutation.isLoading || securityResetWalletMutation.isLoading;

  const close = () => {
    setFormdata({
      currentPassword: "",
      newPassword: "",
      passwordConfirm: ""
    });
    setErrorMessage({
      currentPassword: null,
      newPassword: null,
      passwordConfirm: null
    });
    onHide();
  };

  const isFormValid = () => {
    const { currentPassword, newPassword, passwordConfirm } = formData;
    const { minLength, mixedCase, number, spChar, enablePolicy } =
      passwordPolicy || {};

    if (!enablePolicy) {
      const minLengthCheck = newPassword.length >= (minLength || 0);
      return (
        minLengthCheck &&
        (campaignMode || currentPassword.trim() !== "") &&
        newPassword.trim() !== "" &&
        passwordConfirm.trim() !== "" &&
        newPassword === passwordConfirm
      );
    }

    const minLengthCheck = newPassword.length >= minLength;
    const mixedCaseCheck = mixedCase ? /[A-Z]/.test(newPassword) : true;
    const numberCheck = number ? /\d/.test(newPassword) : true;
    const spCharCheck = spChar ? /[!@#$%^&*]/.test(newPassword) : true;

    return (
      (campaignMode || currentPassword.trim() !== "") &&
      newPassword.trim() !== "" &&
      passwordConfirm.trim() !== "" &&
      newPassword === passwordConfirm &&
      minLengthCheck &&
      mixedCaseCheck &&
      numberCheck &&
      spCharCheck
    );
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormdata((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setErrorMessage((prevData) => ({
      ...prevData,
      [id]: null,
    }));
    if (value === null || value === "") {
      setErrorMessage((prev) => ({
        ...prev,
        [id]: t("required"),
      }));
    }
  };

  const changePassword = () => {
    if (!isFormValid()) {
      const { currentPassword, newPassword, passwordConfirm } = formData;
      if (!campaignMode && currentPassword === "") {
        setErrorMessage((prevData) => ({
          ...prevData,
          currentPassword: t("required"),
        }));
      }

      if (newPassword === "") {
        setErrorMessage((prevData) => ({
          ...prevData,
          newPassword: t("required"),
        }));
      }
      if (newPassword.includes(" ")) {
        setErrorMessage((prevData) => ({
          ...prevData,
          newPassword: "password_cannot_contain_white_spaces",
        }));
      }
      if (passwordConfirm === "") {
        setErrorMessage((prevData) => ({
          ...prevData,
          passwordConfirm: t("required"),
        }));
      } else {
        if (formData.newPassword.length < passwordPolicy?.minLength) {
          setErrorMessage((prevData) => ({
            ...prevData,
            newPassword: t("MinimumlengthIs", { minLength: passwordPolicy?.minLength }),
          }));
        }
      }
      if (passwordPolicy?.enablePolicy) {
        if (passwordPolicy?.mixedCase) {
          if (!/[A-Z]/.test(formData.newPassword)) {
            setErrorMessage((prevData) => ({
              ...prevData,
              newPassword: "Password must contain at least one uppercase letter",
            }));
          }
        }

        if (passwordPolicy?.number) {
          if (!/\d/.test(formData.newPassword)) {
            setErrorMessage((prevData) => ({
              ...prevData,
              newPassword: "Password must contain at least one digit",
            }));
          }
        }

        if (passwordPolicy?.spChar) {
          if (!/[!@#$%^&*]/.test(formData.newPassword)) {
            setErrorMessage((prevData) => ({
              ...prevData,
              newPassword: "Password must contain at least one special character",
            }));
          }
        }
      }
      if (!(formData.newPassword === formData.passwordConfirm)) {
        setErrorMessage((prevData) => ({
          ...prevData,
          passwordConfirm: "Passwords must match",
        }));
      }
      return;
    }

    if (campaignMode) {
      securityResetWalletMutation.mutateAsync(
        {
          password: formData.newPassword,
          passwordConfirm: formData.passwordConfirm,
        },
        {
          onSuccess: (res) => {
            if (res?.status) {
              toast.success(t("wallet_password_updated", "Wallet password updated successfully"));
              close();
            } else {
              setErrorMessage((prevData) => ({
                ...prevData,
                newPassword: t(res?.data?.description || "errorOccuredPleaseTryAgain"),
              }));
            }
          },
        }
      );
      return;
    }

    changeTransactionPasswordMutation.mutateAsync(formData, {
      onSuccess: (res) => {
        if (res?.status) {
          toast.success(t(res?.data));
          close();
          setFormdata({
            currentPassword: "",
            newPassword: "",
            passwordConfirm: ""
          });
        }
        if (res?.code) {
          setErrorMessage((prevData) => ({
            ...prevData,
            currentPassword: t(res?.description)
          }));
        } else {
          console.log(res.message);
        }
      },
    });
  };

  return (
    <Modal show={showModal} onHide={close} dialogClassName="custom-modal-dialog">
      <Modal.Body style={{ padding: "0px" }}>
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="change_password_modal_left_sec">
              <h5 className="modal-title" id="exampleModalLabel">
                {t("resetTransactionPassword")}
              </h5>
              <div className="change_password_image">
                <img src="/images/change_password_img.png" alt="" />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="modal_change_password_right_cnt">
              {campaignMode && (
                <p className="text-muted small mb-2">
                  {t(
                    "security_reset_wallet_session_hint",
                    "During the security reset period you can set a new wallet password without your current one."
                  )}
                </p>
              )}
              {!campaignMode && (
                <div className="form-group mb-2">
                  <Form.Group>
                    <Form.Label>{t("currentTransactionPassword")}</Form.Label>
                    <Form.Control
                      id="currentPassword"
                      type="password"
                      value={formData?.currentPassword}
                      onChange={(e) => handleChange(e)}
                      isInvalid={errorMessage?.currentPassword !== null}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errorMessage.currentPassword}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              )}
              <div className="form-group mb-2">
                <Form.Group>
                  <Form.Label>{t("newTransactionPassword")}</Form.Label>
                  <Form.Control
                    id="newPassword"
                    type="password"
                    value={formData?.newPassword}
                    onChange={(e) => handleChange(e)}
                    isInvalid={errorMessage?.newPassword !== null}
                  />
                  <Form.Control.Feedback type="invalid">
                    {t(errorMessage.newPassword)}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="form-group mb-2">
                <Form.Group>
                  <Form.Label>{t("repeatTransactionPassword")}</Form.Label>
                  <Form.Control
                    id="passwordConfirm"
                    type="password"
                    value={formData?.passwordConfirm}
                    onChange={(e) => handleChange(e)}
                    isInvalid={errorMessage?.passwordConfirm !== null}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errorMessage.passwordConfirm}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={close}>
                  {t("close")}
                </button>
                <SubmitButton
                  className="btn btn-primary"
                  text={isSubmitting ? "updating.." : "saveChanges"}
                  click={changePassword}
                  isSubmitting={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ChangeTransPassModal;
